export const LANGUAGE_LABELS = {
    en: "English",
    en_US: "English",
    de: "German",
    es: "Spanish",
    fr: "French",
    it: "Italian",
    zh_CN: "Chinese",
};

/**
 * Gets available languages from site info.
 * @param {object} siteData - Site data object extracted from redux store
 * @returns {string[]} Array of language codes.
 */
export const getAvailableLanguages = (siteData) => {
    if (siteData?.i18n?.enabled && siteData?.i18n?.locales) {
        return siteData.i18n.locales;
    }

    return ["en"];
};

/**
 * Gets default locale from site info.
 * @param {object} siteData - Site data object extracted from redux store
 * @returns {string} Language code.
 */
export const getDefaultLanguage = (siteData) => siteData?.i18n?.defaultLocale || "en";

/**
 * Check if string contains a language code
 * @param {string} str - String to test
 * @param {string} languageCode - Language code to check
 * @returns {boolean}
 */
export const doesContainLanguageCode = (str, languageCode) => new RegExp(`^/${languageCode}($|/)`).test(str);

export const stripLanguageFromPathname = (pathname, languageCode) =>
    doesContainLanguageCode(pathname, languageCode) ? pathname.replace(`/${languageCode}/`, "/") : pathname;

/**
 * Gets the current language from the URL path.
 * @param {object} siteData - Site data object extracted from redux store
 * @returns {string} The current language code.
 */
export const getCurrentLanguage = (location, availableLanguages) => {
    const pathname = location.pathname;

    for (const lang of availableLanguages) {
        if (doesContainLanguageCode(pathname, lang)) return lang;
    }

    // Return first language as default
    return availableLanguages[0];
};

/**
 * Change the URL based on the triggered language
 * @param {object} location - useLocation hook object
 * @param {object} history - useHistory hook object
 * @param {string} languageCode - New language code
 * @param {string} defaultLanguage - Default language from the redux store site data
 * @param {string[]} availableLanguages - Available languages from the redux store site data
 */
export const changeLanguageUrl = (location, history, languageCode, defaultLanguage, availableLanguages) => {
    // Navigate to the new language URL
    const currentPath = location.pathname;

    // Remove current language prefix from path if present
    let pathWithoutLang = currentPath;
    for (const lang of availableLanguages) {
        if (doesContainLanguageCode(currentPath, lang)) {
            pathWithoutLang = currentPath.substring(lang.length + 1) || "/";
            break;
        }
    }

    // Build new path with new language (skip prefix for default language)
    const newPath = languageCode === defaultLanguage ? pathWithoutLang : `/${languageCode}${pathWithoutLang}`;

    if (currentPath === newPath) return; // Do not change the location if the paths are the same

    history.push(newPath + location.search);
};
